import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { tenantAPI } from '../services/api'
import { errorText } from '../utils/errorText'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [validating, setValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [email, setEmail] = useState('')

  useEffect(() => {
    if (!token) {
      setValidating(false)
      return
    }
    tenantAPI.get(`/auth/reset-password/validate/${token}`)
      .then(res => {
        setTokenValid(res.data.valid)
        setEmail(res.data.email || '')
      })
      .catch(() => setTokenValid(false))
      .finally(() => setValidating(false))
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    setIsLoading(true)
    try {
      await tenantAPI.post('/auth/reset-password/set', { token, password })
      setSuccess(true)
    } catch (err: any) {
      setError(errorText(err, 'Failed to reset password'))
    } finally {
      setIsLoading(false)
    }
  }

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="text-theme-text-secondary">Validating reset link...</div>
      </div>
    )
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary text-center">
          <h2 className="text-2xl font-bold text-theme-text-primary mb-4">Invalid or Expired Link</h2>
          <p className="text-theme-text-secondary mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link to="/forgot-password" className="inline-block py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
            Request New Link
          </Link>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary text-center">
          <h2 className="text-2xl font-bold text-theme-text-primary mb-4">Password Reset</h2>
          <p className="text-theme-text-secondary mb-6">
            Your password has been reset successfully. You can now sign in with your new password.
          </p>
          <Link to="/login" className="inline-block py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
            Sign In
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
      <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
        <div className="flex items-center justify-center space-x-3 mb-6">
          <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
          <h2 className="text-2xl font-bold text-theme-text-primary">Set New Password</h2>
        </div>
        {email && (
          <p className="text-theme-text-secondary text-center mb-4 text-sm">
            Resetting password for <strong>{email}</strong>
          </p>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-1">New Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-1">Confirm Password</label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              placeholder="Confirm your password"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
